import { useCallback, useEffect, useRef, useState } from 'react';
import { api, LoiMayChu } from '../services/api';
import messages from '../locales/vi/joinPlan.json';

export interface JoinPlan {
  id: string;
  actorUid: string;
  deviceId: string;
  targets: { ordinal: number; groupId: string; productStatus: string; membershipStatus: string }[];
}

export function JoinPlanPanel({ actorUid, onAccepted }: { actorUid?: string; onAccepted: (batchId: string) => void }) {
  const [plans, setPlans] = useState<JoinPlan[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [recoveryBlocked, setRecoveryBlocked] = useState(true);
  const lock = useRef(false);
  const generation = useRef(0);
  const intentStorageKey = 'fbmax.joinPlan.pendingIntent';
  const load = useCallback(async () => {
    const request = ++generation.current;
    try {
      const result = await api.getJoinPlans(actorUid);
      const policy = await api.getGroupJoinPolicy(actorUid);
      if (!Array.isArray(result.plans) || result.plans.some(plan => !plan || typeof plan.id !== 'string' || !Array.isArray(plan.targets) || plan.targets.some(target => !target || typeof target.groupId !== 'string' || typeof target.productStatus !== 'string'))) throw new Error('PLAN_RESPONSE_INVALID');
      if (generation.current === request) {
        setPlans(result.plans);
        setRecoveryBlocked(Boolean(policy.actorQuota?.isBlocked));
        // This records only unresolved intent metadata, never authorization.
        // Remount/reload must not turn a lost response into a new submission.
        const saved = sessionStorage.getItem(intentStorageKey);
        if (!saved) { setUncertain(false); setNotice(null); }
        if (saved) {
          const intent = JSON.parse(saved) as { planId: string; groupId: string };
          const target = result.plans.find(plan => plan.id === intent.planId)?.targets.find(target => target.groupId === intent.groupId);
          if (target && ['QUEUED','RUNNING','PAUSED','UNKNOWN_RESULT','JOINED','REQUESTED_PENDING','NEEDS_USER_ACTION','UNAVAILABLE','SKIPPED','FAILED','CANCELLED'].includes(target.productStatus)) {
            sessionStorage.removeItem(intentStorageKey);
            setUncertain(false);
          } else {
            setUncertain(true);
            setNotice(messages.uncertain);
          }
        }
      }
    } catch {
      if (generation.current === request) { setUncertain(true); setNotice(messages.readError); }
    }
  }, [actorUid]);
  useEffect(() => { setPlans([]); load(); return () => { generation.current++; }; }, [load]);

  const submit = async (plan: JoinPlan) => {
    if (lock.current || uncertain) return;
    lock.current = true;
    const target = plan.targets.find(target => target.productStatus === 'UNSUBMITTED' && !['MEMBER', 'PENDING_APPROVAL'].includes(target.membershipStatus));
    try {
      if (!target || sessionStorage.getItem(intentStorageKey)) { lock.current = false; return; }
      sessionStorage.setItem(intentStorageKey, JSON.stringify({ planId: plan.id, groupId: target.groupId }));
    } catch { lock.current = false; setUncertain(true); setNotice(messages.uncertain); return; }
    setBusy(true);
    setNotice(messages.sending);
    const request = ++generation.current;
    try {
      const result = await api.nextJoinPlanTarget(plan.id, target.groupId);
      if (generation.current !== request) return;
      const batchId=result.batch?.batchId || result.batch?.id;
      if (result.ok !== true || typeof result.completed !== 'boolean' || (!result.completed && typeof batchId !== 'string')) throw new Error('PLAN_RESPONSE_INVALID');
      if (batchId) {
        sessionStorage.removeItem(intentStorageKey);
        onAccepted(batchId);
        setNotice(messages.accepted);
      }
      await load();
    } catch (error) {
      if (generation.current !== request) return;
      const code = error instanceof LoiMayChu ? error.ma : null;
      if (!(error instanceof LoiMayChu) || (error.dulieu as {admission?:string})?.admission !== 'REJECTED_BEFORE_MATERIALIZATION') {
        setUncertain(true);
        setNotice(messages.uncertain);
      } else {
        sessionStorage.removeItem(intentStorageKey);
        setNotice(code === 'PLAN_EXPECTED_TARGET_REQUIRED' ? messages.updateRequired : messages.error + (code || 'PLAN_NEXT_REJECTED'));
      }
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  // Recovered actors use the normal single-submit list workflow, not the old
  // investigation-only per-target recovery action.
  if (!recoveryBlocked || (!plans.length && !notice)) return null;
  return <section className="space-y-3 rounded-xl border border-sky-200 bg-white p-5 dark:bg-slate-900" aria-label={messages.title}>
    <h3 className="font-bold">{messages.title}</h3>
    {plans.map(plan => {
      const pending = plan.targets.some(target => ['QUEUED', 'RUNNING', 'PAUSED'].includes(target.productStatus));
      const next = plan.targets.find(target => target.productStatus === 'UNSUBMITTED' && !['MEMBER', 'PENDING_APPROVAL'].includes(target.membershipStatus));
      return <div key={plan.id} className="space-y-2" data-plan-id={plan.id}>
        <p>{messages.total}: {plan.targets.length} · {messages.remaining}: {plan.targets.filter(target => target.productStatus === 'UNSUBMITTED').length} · {messages.joined}: {plan.targets.filter(target => target.productStatus === 'JOINED').length}</p>
        <details className="text-xs text-slate-500"><summary>{messages.details}</summary><p>{messages.description}</p><p>{messages.unknown}: {plan.targets.filter(target => target.productStatus === 'UNKNOWN_RESULT').length}</p></details>
        {next && <p>{messages.next}: <strong>{next.groupId}</strong></p>}
        {pending && <p>{messages.pending}</p>}
        <button type="button" className="rounded-lg bg-brand-600 px-4 py-2 text-white disabled:opacity-50" disabled={busy || uncertain || pending || !next || plan.actorUid !== actorUid} onClick={() => submit(plan)}>{busy ? messages.sending : messages.submit}</button>
      </div>;
    })}
    {notice && <p role="status">{notice}</p>}
    <button type="button" disabled={busy} onClick={load}>{messages.refresh}</button>
  </section>;
}
